/* ═══════════════════════════════════════════════════════════════
   PlanGrid — the sparse person↔project × week grid (Mode 1: Plan).

   Rows: person → project sub-rows (or transposed: project → person),
   collapsed by default showing just the parent rollup (D5 — no empty
   "Mine" wall, no cartesian matrix). Columns: rolling week horizon,
   current week marked. Cells: weekly totals, type-in-cell, Tab/Enter/
   arrow nav, copy/cut/paste (Ctrl+C/X/V + right-click menu), fill-right
   drag handle. Vacation rows locked. Unforecasted pairings flagged.
   One caution treatment for over-allocation — no four-color alarm soup.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronRight, Plus, Copy, Scissors, Clipboard, Grid2x2 } from 'lucide-react';
import type {
  TeamMember,
  Department,
  Team,
  TeamAssignment,
  ProjectAssignment,
  HourForecast,
  Project,
} from '../../../shared/types';
import { TimeService } from '../../services/timeService';
import { SearchableSelect } from '../../components/SearchableSelect';
import { AnchoredPopover } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import {
  buildPersonPivotRows,
  buildProjectPivotRows,
  bucketByWeek,
  sumHours,
} from './buildPlanRows';
import type { GroupData } from './buildPlanRows';
import type { PairingRow, Pivot, PersonLayout, ProjectBreakdown, HorizonWeeks } from './types';
import {
  addDays,
  formatShort,
  initialsOf,
  mondayOf,
  splitWeekEvenly,
  todayIso,
  weekDays,
  weekWindow,
} from './utils';
import { DayExpansionDrawer } from './DayExpansionDrawer';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';

const LABEL_COL = 260;
const WEEK_COL = 96;

interface PlanGridProps {
  members: TeamMember[];
  projects: Project[];
  allProjects: Project[];
  forecasts: HourForecast[];
  departments: Department[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  projectAssignments: ProjectAssignment[];
  pivot: Pivot;
  personLayout: PersonLayout;
  projectBreakdown: ProjectBreakdown;
  horizonWeeks: HorizonWeeks;
  windowStart: string;
  onWindowStartChange: (next: string) => void;
  canEditCell: (memberId: string) => boolean;
  onRefetch: () => Promise<void> | void;
  toast?: (msg: string, isError?: boolean) => void;
}

interface EditableRowEntry {
  rowId: string;
  pairing: PairingRow;
  weekTotals: Map<string, number>;
}

export function PlanGrid({
  members,
  projects,
  allProjects,
  forecasts,
  departments,
  teams,
  teamAssignments,
  projectAssignments,
  pivot,
  personLayout,
  projectBreakdown,
  horizonWeeks,
  windowStart,
  onWindowStartChange,
  canEditCell,
  onRefetch,
  toast,
}: PlanGridProps) {
  const weeks = useMemo(() => weekWindow(windowStart, horizonWeeks), [windowStart, horizonWeeks]);
  const currentWeek = useMemo(() => mondayOf(todayIso()), []);

  const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(new Set());
  const [pendingPairings, setPendingPairings] = useState<Map<string, Set<string>>>(new Map());
  const [addProjectOpenFor, setAddProjectOpenFor] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Map<string, string>>(new Map());
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [focusedCell, setFocusedCell] = useState<{ rowId: string; week: string } | null>(null);
  const [drawerFor, setDrawerFor] = useState<{ pairing: PairingRow; week: string } | null>(null);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    rowId: string;
    week: string;
    anchor: DOMRect;
  } | null>(null);

  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const clipboardRef = useRef<number | null>(null);
  const fillDragRef = useRef<{ rowId: string; startWeekIdx: number; value: number } | null>(null);
  const [fillPreviewWeekIdx, setFillPreviewWeekIdx] = useState<number | null>(null);

  const groups: GroupData[] = useMemo(() => {
    if (pivot === 'person') {
      return buildPersonPivotRows({
        members,
        projects,
        forecasts,
        weeks,
        layout: personLayout,
        departments,
        teams,
        teamAssignments,
        projectAssignments,
        pendingPairings,
      });
    }
    return buildProjectPivotRows({
      members,
      projects,
      forecasts,
      weeks,
      breakdown: projectBreakdown,
      teams,
      teamAssignments,
      projectAssignments,
      departments,
    });
  }, [
    pivot,
    members,
    projects,
    forecasts,
    weeks,
    personLayout,
    projectBreakdown,
    departments,
    teams,
    teamAssignments,
    projectAssignments,
    pendingPairings,
  ]);

  // Per-pairing weekly totals, computed once from the raw forecasts so
  // every row render reuses the same bucketed data instead of re-scanning.
  const weekTotalsByPairing = useMemo(() => {
    const out = new Map<string, Map<string, number>>();
    for (const group of groups) {
      for (const parent of group.parents) {
        for (const pairing of parent.pairings) {
          const rows = forecasts.filter(
            (f) =>
              f.teamMemberId === pairing.memberId &&
              (pairing.isVacation
                ? f.sourceKind === 'vacation'
                : f.projectId === pairing.projectId && f.sourceKind !== 'vacation')
          );
          const byWeek = bucketByWeek(rows);
          const totals = new Map<string, number>();
          for (const wk of weeks) totals.set(wk, sumHours(byWeek.get(wk)));
          out.set(pairing.id, totals);
        }
      }
    }
    return out;
  }, [groups, forecasts, weeks]);

  const parentWeekTotals = useMemo(() => {
    const out = new Map<string, { total: Map<string, number>; vacation: Map<string, number> }>();
    for (const group of groups) {
      for (const parent of group.parents) {
        const total = new Map<string, number>();
        const vacation = new Map<string, number>();
        for (const wk of weeks) {
          let t = 0;
          let v = 0;
          for (const pairing of parent.pairings) {
            const wt = weekTotalsByPairing.get(pairing.id)?.get(wk) || 0;
            t += wt;
            if (pairing.isVacation) v += wt;
          }
          total.set(wk, t);
          vacation.set(wk, v);
        }
        out.set(parent.id, { total, vacation });
      }
    }
    return out;
  }, [groups, weeks, weekTotalsByPairing]);

  const editableRows: EditableRowEntry[] = useMemo(() => {
    const out: EditableRowEntry[] = [];
    for (const group of groups) {
      for (const parent of group.parents) {
        if (!expandedParentIds.has(parent.id)) continue;
        for (const pairing of parent.pairings) {
          if (pairing.editable && pairing.memberId && canEditCell(pairing.memberId)) {
            out.push({
              rowId: pairing.id,
              pairing,
              weekTotals: weekTotalsByPairing.get(pairing.id) || new Map(),
            });
          }
        }
      }
    }
    return out;
  }, [groups, expandedParentIds, weekTotalsByPairing, canEditCell]);

  const toggleParent = (id: string) => {
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ─── Cell commit (writes an even Mon–Fri split server-side) ─────────
  const commitCell = useCallback(
    async (pairing: PairingRow, week: string, hours: number) => {
      if (!pairing.memberId || !pairing.editable || pairing.isVacation || !pairing.projectId)
        return;
      const key = `${pairing.id}::${week}`;
      setSavingKeys((prev) => new Set(prev).add(key));
      try {
        const days = weekDays(week).slice(0, 5); // Mon–Fri
        const values = hours > 0 ? splitWeekEvenly(hours) : [0, 0, 0, 0, 0];
        for (let i = 0; i < 5; i++) {
          await TimeService.setForecastDayCell({
            teamMemberId: pairing.memberId,
            projectId: pairing.projectId,
            dayDate: days[i],
            hours: values[i],
          });
        }
        setPendingPairings((prev) => {
          const next = new Map(prev);
          const s = next.get(pairing.memberId!);
          if (s?.has(pairing.projectId!)) {
            const ns = new Set(s);
            ns.delete(pairing.projectId!);
            next.set(pairing.memberId!, ns);
          }
          return next;
        });
        await onRefetch();
      } catch (err) {
        toast?.(
          `Couldn't save hours: ${err instanceof Error ? err.message : 'unknown error'}`,
          true
        );
      } finally {
        setDrafts((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        setSavingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [onRefetch, toast]
  );

  const commitCellByRowId = useCallback(
    (rowId: string, week: string, hours: number) => {
      const entry = editableRows.find((r) => r.rowId === rowId);
      if (entry) commitCell(entry.pairing, week, hours);
    },
    [editableRows, commitCell]
  );

  // ─── Keyboard / focus navigation ─────────────────────────────────────
  function cellKey(rowId: string, week: string): string {
    return `${rowId}::${week}`;
  }
  function focusCellInput(rowId: string, week: string) {
    const el = inputRefs.current.get(cellKey(rowId, week));
    el?.focus();
    el?.select();
  }
  function moveRow(rowId: string, week: string, delta: 1 | -1) {
    const idx = editableRows.findIndex((r) => r.rowId === rowId);
    if (idx === -1) return;
    const next = editableRows[idx + delta];
    if (next) focusCellInput(next.rowId, week);
  }
  function moveWeek(rowId: string, weekIdx: number, delta: 1 | -1) {
    const nextIdx = weekIdx + delta;
    if (nextIdx < 0 || nextIdx >= weeks.length) return;
    focusCellInput(rowId, weeks[nextIdx]);
  }

  function handleCellKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    entry: EditableRowEntry,
    week: string,
    weekIdx: number
  ) {
    const input = e.currentTarget;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      handleCopy(entry.rowId, week, false);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      handleCopy(entry.rowId, week, true);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      handlePaste(entry.rowId, week);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const raw = drafts.get(cellKey(entry.rowId, week));
      commitCell(
        entry.pairing,
        week,
        raw != null ? parseFloat(raw) || 0 : entry.weekTotals.get(week) || 0
      );
      moveRow(entry.rowId, week, 1);
      return;
    }
    if (e.key === 'Escape') {
      setDrafts((prev) => {
        const next = new Map(prev);
        next.delete(cellKey(entry.rowId, week));
        return next;
      });
      input.blur();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveRow(entry.rowId, week, -1);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveRow(entry.rowId, week, 1);
      return;
    }
    if (e.key === 'ArrowLeft' && input.selectionStart === 0) {
      e.preventDefault();
      moveWeek(entry.rowId, weekIdx, -1);
      return;
    }
    if (e.key === 'ArrowRight' && input.selectionStart === input.value.length) {
      e.preventDefault();
      moveWeek(entry.rowId, weekIdx, 1);
    }
  }

  function handleCopy(rowId: string, week: string, cut: boolean) {
    const entry = editableRows.find((r) => r.rowId === rowId);
    if (!entry) return;
    const raw = drafts.get(cellKey(rowId, week));
    const val = raw != null ? parseFloat(raw) || 0 : entry.weekTotals.get(week) || 0;
    clipboardRef.current = val;
    navigator.clipboard?.writeText(String(val)).catch(() => {});
    if (cut) commitCell(entry.pairing, week, 0);
  }
  function handlePaste(rowId: string, week: string) {
    if (clipboardRef.current == null) return;
    commitCellByRowId(rowId, week, clipboardRef.current);
  }

  // ─── Fill-right drag handle ───────────────────────────────────────
  const onDocMouseMoveRef = useRef<(e: MouseEvent) => void>();
  const onDocMouseUpRef = useRef<(e: MouseEvent) => void>();
  function startFillDrag(rowId: string, weekIdx: number, value: number) {
    fillDragRef.current = { rowId, startWeekIdx: weekIdx, value };
    setFillPreviewWeekIdx(weekIdx);
    onDocMouseMoveRef.current = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const cellEl = el?.closest('[data-cell-row]') as HTMLElement | null;
      if (!cellEl || cellEl.dataset.cellRow !== rowId) return;
      const w = Number(cellEl.dataset.cellWeekIdx);
      if (!Number.isNaN(w)) setFillPreviewWeekIdx(w);
    };
    onDocMouseUpRef.current = () => {
      document.removeEventListener('mousemove', onDocMouseMoveRef.current!);
      document.removeEventListener('mouseup', onDocMouseUpRef.current!);
      const drag = fillDragRef.current;
      fillDragRef.current = null;
      setFillPreviewWeekIdx((endIdx) => {
        if (drag && endIdx != null && endIdx > drag.startWeekIdx) {
          const entry = editableRows.find((r) => r.rowId === drag.rowId);
          if (entry) {
            for (let i = drag.startWeekIdx + 1; i <= endIdx; i++) {
              commitCell(entry.pairing, weeks[i], drag.value);
            }
          }
        }
        return null;
      });
    };
    document.addEventListener('mousemove', onDocMouseMoveRef.current);
    document.addEventListener('mouseup', onDocMouseUpRef.current);
  }

  // ─── Day expansion drawer ───────────────────────────────────────────
  function openDrawer(pairing: PairingRow, week: string) {
    setDrawerFor({ pairing, week });
  }
  function drawerHoursByDay(): Record<string, number> {
    if (!drawerFor) return {};
    const days = weekDays(drawerFor.week);
    const rows = forecasts.filter(
      (f) =>
        f.teamMemberId === drawerFor.pairing.memberId &&
        f.projectId === drawerFor.pairing.projectId &&
        f.sourceKind !== 'vacation'
    );
    const out: Record<string, number> = {};
    for (const d of days) out[d] = 0;
    for (const f of rows) {
      const day = f.dayDate;
      if (day && day in out) out[day] += Number(f.hours) || 0;
    }
    return out;
  }
  async function saveDrawer(next: Record<string, number>) {
    if (!drawerFor?.pairing.memberId || !drawerFor?.pairing.projectId) return;
    setDrawerSaving(true);
    try {
      for (const [day, hours] of Object.entries(next)) {
        await TimeService.setForecastDayCell({
          teamMemberId: drawerFor.pairing.memberId,
          projectId: drawerFor.pairing.projectId,
          dayDate: day,
          hours,
        });
      }
      await onRefetch();
      setDrawerFor(null);
    } catch (err) {
      toast?.(
        `Couldn't save day hours: ${err instanceof Error ? err.message : 'unknown error'}`,
        true
      );
    } finally {
      setDrawerSaving(false);
    }
  }
  async function clearDrawer() {
    if (!drawerFor) return;
    const zeros: Record<string, number> = {};
    for (const d of weekDays(drawerFor.week)) zeros[d] = 0;
    await saveDrawer(zeros);
  }

  const isEmpty = groups.every((g) => g.parents.length === 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Week window nav */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-4)',
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => onWindowStartChange(addDays(windowStart, -7))}
          aria-label="Previous week"
          style={navBtnStyle}
        >
          ‹
        </button>
        <button
          onClick={() => onWindowStartChange(currentWeek)}
          style={{
            ...navBtnStyle,
            width: 'auto',
            padding: `var(--space-1) ${rem(10)}`,
            fontWeight: 600,
          }}
        >
          Today
        </button>
        <button
          onClick={() => onWindowStartChange(addDays(windowStart, 7))}
          aria-label="Next week"
          style={navBtnStyle}
        >
          ›
        </button>
        <span style={{ fontSize: 'calc(var(--font-size-base) * 12 / 13)', color: 'var(--tm)' }}>
          {formatShort(weeks[0])} – {formatShort(addDays(weeks[weeks.length - 1], 6))}
        </span>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }} onClick={() => setContextMenu(null)}>
        {isEmpty ? (
          <EmptyState
            title="No one to plan yet"
            subtitle="Widen your scope/filters, or check back once assignments exist."
          />
        ) : (
          <div
            style={{
              minWidth: LABEL_COL + weeks.length * WEEK_COL,
              display: 'grid',
              gridTemplateColumns: `${LABEL_COL}px repeat(${weeks.length}, ${WEEK_COL}px)`,
            }}
          >
            {/* Header */}
            <div style={headerCellStyle}>{pivot === 'person' ? 'People' : 'Projects'}</div>
            {weeks.map((wk) => (
              <div
                key={wk}
                style={{
                  ...headerCellStyle,
                  textAlign: 'center',
                  background:
                    wk === currentWeek
                      ? 'color-mix(in srgb, var(--ac) 8%, var(--sf))'
                      : 'var(--sf)',
                  color: wk === currentWeek ? 'var(--ac)' : 'var(--tx)',
                }}
              >
                {formatShort(wk)}
                {wk === currentWeek && (
                  <div
                    style={{ fontSize: 'calc(var(--font-size-base) * 9 / 13)', fontWeight: 700 }}
                  >
                    THIS WEEK
                  </div>
                )}
              </div>
            ))}

            {groups.map((group) => (
              <React.Fragment key={group.id}>
                {group.label && (
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      padding: `${rem(6)} ${rem(14)}`,
                      background: 'var(--sa)',
                      borderTop: '1px solid var(--bd)',
                      borderBottom: '1px solid var(--bd)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--tx)',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                    }}
                  >
                    {group.color && (
                      <span
                        aria-hidden="true"
                        style={{
                          width: rem(10),
                          height: rem(10),
                          borderRadius: rem(3),
                          background: group.color,
                        }}
                      />
                    )}
                    {group.label}
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        fontWeight: 500,
                      }}
                    >
                      ({group.parents.length})
                    </span>
                  </div>
                )}
                {group.parents.length === 0 && (
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      padding: `${rem(10)} ${rem(14)}`,
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      color: 'var(--tm)',
                    }}
                  >
                    Nothing here yet.
                  </div>
                )}
                {group.parents.map((parent) => {
                  const expanded = expandedParentIds.has(parent.id);
                  const totals = parentWeekTotals.get(parent.id);
                  const existingProjectIds = new Set(
                    parent.pairings.filter((p) => p.projectId).map((p) => p.projectId as string)
                  );
                  return (
                    <React.Fragment key={parent.id}>
                      <button
                        onClick={() => toggleParent(parent.id)}
                        aria-expanded={expanded}
                        style={{
                          padding: `${rem(9)} ${rem(14)}`,
                          border: 'none',
                          borderBottom: '1px solid var(--bd)',
                          background: 'var(--sf)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          cursor: 'pointer',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          textAlign: 'left',
                          font: 'inherit',
                          color: 'inherit',
                        }}
                      >
                        <Icon
                          icon={ChevronRight}
                          size="md"
                          style={{
                            transform: expanded ? 'rotate(90deg)' : 'none',
                            transition: 'transform .12s',
                            flexShrink: 0,
                            color: 'var(--tm)',
                          }}
                        />
                        {parent.kind === 'member' && (
                          <span
                            aria-hidden="true"
                            style={{
                              width: 'var(--space-5)',
                              height: 'var(--space-5)',
                              borderRadius: '50%',
                              background: 'var(--ac)',
                              color: 'var(--color-text-inverse)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {initialsOf(parent.label)}
                          </span>
                        )}
                        {parent.kind === 'project' && (
                          <span
                            aria-hidden="true"
                            style={{
                              width: rem(10),
                              height: rem(10),
                              borderRadius: rem(3),
                              background: parent.color,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <span
                          style={{
                            fontSize: 'var(--font-size-base)',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                          title={parent.label}
                        >
                          {parent.label}
                        </span>
                      </button>
                      {weeks.map((wk) => {
                        const total = totals?.total.get(wk) || 0;
                        const vac = totals?.vacation.get(wk) || 0;
                        const member =
                          parent.kind === 'member'
                            ? members.find((m) => m.id === parent.refId)
                            : undefined;
                        let overAllocated = false;
                        if (member) {
                          const isPartTime = member.employmentType === 'part_time_hourly';
                          const rawTarget = member.weeklyCapacityHours ?? (isPartTime ? 20 : 40);
                          const available = Math.max(0, rawTarget - vac);
                          const worked = total - vac;
                          overAllocated = available > 0 && worked > available * 1.1;
                        }
                        return (
                          <div
                            key={wk}
                            style={{
                              padding: `${rem(9)} var(--space-2)`,
                              borderBottom: '1px solid var(--bd)',
                              borderLeft: '1px solid var(--bd)',
                              background:
                                wk === currentWeek
                                  ? 'color-mix(in srgb, var(--ac) 3%, transparent)'
                                  : undefined,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              color: overAllocated
                                ? 'var(--color-caution)'
                                : total > 0
                                  ? 'var(--tx)'
                                  : 'var(--tm)',
                            }}
                            title={overAllocated ? 'Over-allocated this week' : undefined}
                          >
                            {total > 0 ? `${total}h` : '—'}
                          </div>
                        );
                      })}

                      {expanded &&
                        parent.pairings.map((pairing) => {
                          const editable =
                            pairing.editable && !!pairing.memberId && canEditCell(pairing.memberId);
                          const totals = weekTotalsByPairing.get(pairing.id) || new Map();
                          return (
                            <React.Fragment key={pairing.id}>
                              <div
                                style={{
                                  padding: `${rem(7)} ${rem(14)} ${rem(7)} ${rem(40)}`,
                                  borderBottom: '1px solid var(--bd)',
                                  background: pairing.unforecasted
                                    ? 'color-mix(in srgb, var(--color-warning) 6%, var(--sf))'
                                    : pairing.isVacation
                                      ? 'color-mix(in srgb, var(--tm) 5%, var(--sf))'
                                      : 'var(--sf)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 'var(--space-2)',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                }}
                              >
                                <span
                                  aria-hidden="true"
                                  style={{
                                    width: rem(9),
                                    height: rem(9),
                                    borderRadius: rem(2),
                                    background: pairing.color || 'var(--bd)',
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                                    fontWeight: 500,
                                    color: pairing.isVacation ? 'var(--tm)' : 'var(--tx)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                  title={pairing.label}
                                >
                                  {pairing.label}
                                </span>
                                {pairing.hint && (
                                  <span
                                    style={{
                                      fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                                      color: 'var(--tm)',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {pairing.hint}
                                  </span>
                                )}
                                {pairing.unforecasted && (
                                  <StatusBadge
                                    status="unforecasted"
                                    label="Unforecasted"
                                    variant="warning"
                                  />
                                )}
                              </div>
                              {weeks.map((wk, weekIdx) => {
                                const key = cellKey(pairing.id, wk);
                                const draft = drafts.get(key);
                                const computed = totals.get(wk) || 0;
                                const displayValue =
                                  draft != null ? draft : computed === 0 ? '' : String(computed);
                                const isFocused =
                                  focusedCell?.rowId === pairing.id && focusedCell.week === wk;
                                const isFillPreview =
                                  fillDragRef.current?.rowId === pairing.id &&
                                  fillPreviewWeekIdx != null &&
                                  weekIdx > fillDragRef.current.startWeekIdx &&
                                  weekIdx <= fillPreviewWeekIdx;

                                if (!editable) {
                                  return (
                                    <div
                                      key={wk}
                                      style={{
                                        padding: `${rem(6)} var(--space-2)`,
                                        borderBottom: '1px solid var(--bd)',
                                        borderLeft: '1px solid var(--bd)',
                                        background: 'color-mix(in srgb, var(--tm) 4%, transparent)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                                        color: 'var(--tm)',
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                      title={
                                        pairing.isVacation
                                          ? 'Vacation — locked, synced from an approved request'
                                          : 'Read-only — analytic rollup'
                                      }
                                    >
                                      {computed > 0 ? `${computed}h` : '—'}
                                    </div>
                                  );
                                }
                                const entry = { rowId: pairing.id, pairing, weekTotals: totals };
                                return (
                                  <div
                                    key={wk}
                                    data-cell-row={pairing.id}
                                    data-cell-week-idx={weekIdx}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      setContextMenu({
                                        rowId: pairing.id,
                                        week: wk,
                                        anchor: {
                                          left: e.clientX,
                                          top: e.clientY,
                                          right: e.clientX,
                                          bottom: e.clientY,
                                          width: 0,
                                          height: 0,
                                          x: e.clientX,
                                          y: e.clientY,
                                          toJSON: () => ({}),
                                        } as DOMRect,
                                      });
                                    }}
                                    style={{
                                      position: 'relative',
                                      padding: rem(2),
                                      borderBottom: '1px solid var(--bd)',
                                      borderLeft: '1px solid var(--bd)',
                                      background: isFillPreview
                                        ? 'color-mix(in srgb, var(--ac) 10%, transparent)'
                                        : undefined,
                                    }}
                                  >
                                    <input
                                      ref={(el) => {
                                        if (el) inputRefs.current.set(key, el);
                                        else inputRefs.current.delete(key);
                                      }}
                                      type="text"
                                      inputMode="decimal"
                                      value={displayValue}
                                      placeholder="—"
                                      onFocus={() =>
                                        setFocusedCell({ rowId: pairing.id, week: wk })
                                      }
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                          setDrafts((prev) => new Map(prev).set(key, v));
                                        }
                                      }}
                                      onBlur={() => {
                                        const raw = drafts.get(key);
                                        if (raw != null)
                                          commitCell(pairing, wk, parseFloat(raw) || 0);
                                        setFocusedCell(null);
                                      }}
                                      onKeyDown={(e) => handleCellKeyDown(e, entry, wk, weekIdx)}
                                      disabled={savingKeys.has(key)}
                                      aria-label={`${pairing.label} — week of ${formatShort(wk)}`}
                                      style={{
                                        width: '100%',
                                        height: rem(34),
                                        border: 'none',
                                        outline: isFocused ? '2px solid var(--ac)' : 'none',
                                        outlineOffset: -2,
                                        borderRadius: 'var(--radius-sm)',
                                        background: 'transparent',
                                        color: 'var(--tx)',
                                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        fontVariantNumeric: 'tabular-nums',
                                        opacity: savingKeys.has(key) ? 0.5 : 1,
                                      }}
                                      onDoubleClick={() => openDrawer(pairing, wk)}
                                      title="Type a weekly total (even Mon–Fri split). Double-click for the per-day breakdown."
                                    />
                                    {isFocused && (
                                      <div
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const raw = drafts.get(key);
                                          const val = raw != null ? parseFloat(raw) || 0 : computed;
                                          startFillDrag(pairing.id, weekIdx, val);
                                        }}
                                        title="Drag to fill hours into the weeks to the right"
                                        style={{
                                          position: 'absolute',
                                          right: rem(1),
                                          bottom: rem(1),
                                          width: 'var(--space-2)',
                                          height: 'var(--space-2)',
                                          background: 'var(--ac)',
                                          borderRadius: rem(1),
                                          cursor: 'ew-resize',
                                        }}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}

                      {expanded && parent.kind === 'member' && canEditCell(parent.refId) && (
                        <div
                          style={{
                            gridColumn: '1 / -1',
                            borderBottom: '1px solid var(--bd)',
                            padding: `var(--space-1) ${rem(14)} var(--space-1) ${rem(40)}`,
                          }}
                        >
                          {addProjectOpenFor === parent.id ? (
                            <div style={{ maxWidth: rem(280) }}>
                              <SearchableSelect
                                value=""
                                options={allProjects
                                  .filter((p) => !existingProjectIds.has(p.id))
                                  .map((p) => ({
                                    value: p.id,
                                    label: p.name,
                                    hint: p.projectNumber,
                                  }))}
                                onChange={(projectId) => {
                                  if (!projectId) return;
                                  setPendingPairings((prev) => {
                                    const next = new Map(prev);
                                    const s = new Set(next.get(parent.refId) || []);
                                    s.add(projectId);
                                    next.set(parent.refId, s);
                                    return next;
                                  });
                                  setExpandedParentIds((prev) => new Set(prev).add(parent.id));
                                  setAddProjectOpenFor(null);
                                }}
                                placeholder="Pick a project…"
                                clearable={false}
                                autoOpenOnFocus
                              />
                            </div>
                          ) : (
                            <button
                              onClick={() => setAddProjectOpenFor(parent.id)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                background: 'none',
                                border: 'none',
                                color: 'var(--ac)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                padding: 'var(--space-1) 2px',
                              }}
                            >
                              <Icon icon={Plus} em={1.0909} /> Add project
                            </button>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {contextMenu && (
        <AnchoredPopover
          anchorRect={contextMenu.anchor}
          width={160}
          onClose={() => setContextMenu(null)}
        >
          <div style={{ padding: 'var(--space-1)' }}>
            <ContextMenuItem
              icon={<Icon icon={Copy} size="md" />}
              onClick={() => {
                handleCopy(contextMenu.rowId, contextMenu.week, false);
                setContextMenu(null);
              }}
            >
              Copy
            </ContextMenuItem>
            <ContextMenuItem
              icon={<Icon icon={Scissors} size="md" />}
              onClick={() => {
                handleCopy(contextMenu.rowId, contextMenu.week, true);
                setContextMenu(null);
              }}
            >
              Cut
            </ContextMenuItem>
            <ContextMenuItem
              icon={<Icon icon={Clipboard} size="md" />}
              disabled={clipboardRef.current == null}
              onClick={() => {
                handlePaste(contextMenu.rowId, contextMenu.week);
                setContextMenu(null);
              }}
            >
              Paste
            </ContextMenuItem>
            <ContextMenuItem
              icon={<Icon icon={Grid2x2} size="md" />}
              onClick={() => {
                const entry = editableRows.find((r) => r.rowId === contextMenu.rowId);
                if (entry) openDrawer(entry.pairing, contextMenu.week);
                setContextMenu(null);
              }}
            >
              Per-day breakdown…
            </ContextMenuItem>
          </div>
        </AnchoredPopover>
      )}

      {drawerFor && (
        <DayExpansionDrawer
          open={!!drawerFor}
          onOpenChange={(o) => !o && setDrawerFor(null)}
          title={`${drawerFor.pairing.label} — ${formatShort(drawerFor.week)}`}
          weekStart={drawerFor.week}
          hoursByDay={drawerHoursByDay()}
          readOnly={!drawerFor.pairing.memberId || !canEditCell(drawerFor.pairing.memberId)}
          saving={drawerSaving}
          onSave={saveDrawer}
          onClear={clearDrawer}
        />
      )}
    </div>
  );
}

function ContextMenuItem({
  children,
  onClick,
  icon,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      role="menuitem"
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: `${rem(6)} ${rem(10)}`,
        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
        background: 'none',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        color: disabled ? 'var(--tm)' : 'var(--tx)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

const headerCellStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--sf)',
  borderBottom: '1px solid var(--bd)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const navBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--bd)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  width: rem(26),
  height: rem(26),
  borderRadius: rem(5),
  cursor: 'pointer',
};
